<script setup lang="ts">
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { Modal, Card, Button, Spin, Input, Checkbox } from 'ant-design-vue'
  import { PlusOutlined, SearchOutlined, WarningFilled } from '@ant-design/icons-vue'
  import { TableSchema } from '../models/TableSchema'

  const props = defineProps<{
    visible: boolean
    tableSchemas: Array<TableSchema>
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
    'table-selected': [table: TableSchema]
  }>()

  const availableTables = ref<TableSchema[]>([])
  const filteredTables = ref<TableSchema[]>([])
  const showHiddenTables = ref<boolean>(false)
  const loadingTables = ref<boolean>(false)
  const hasErrorLoadingTables = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchInputRef = ref<InstanceType<typeof Input> | null>(null)

  // Group tables by category
  const groupedTables = ref<Record<string, TableSchema[]>>({})

  const onSearchTextChanged = (event: InputEvent): void => {
    const target = event.target as HTMLInputElement
    const value = target.value
    const search = value?.toLowerCase().trim()

    if (search?.length > 0) {
      filteredTables.value = availableTables.value.filter((tableSchema: TableSchema) => {
        return (
          (tableSchema.description && tableSchema.description.toLowerCase().includes(search)) ||
          (tableSchema.display_name && tableSchema.display_name.toLowerCase().includes(search))
        )
      })
    } else {
      filteredTables.value = [...availableTables.value]
    }
    groupTablesByCategory()
  }

  const groupTablesByCategory = () => {
    const grouped: Record<string, TableSchema[]> = {}
    filteredTables.value.forEach((table) => {
      if (!grouped[table.category]) {
        grouped[table.category] = []
      }
      grouped[table.category].push(table)
    })
    groupedTables.value = grouped
  }

  const selectTable = (table: TableSchema) => {
    emit('table-selected', table)
    // Keep dialog open to allow adding multiple tables easily
  }

  const handleCancel = () => {
    emit('update:visible', false)
  }

  onMounted(() => {
    resetAvailableTables()
  })

  const handleVisibilityChange = (visible: boolean) => {
    if (visible) {
      resetAvailableTables()
      nextTick(() => {
        // reset the search model (don't overwrite the Input ref)
        searchText.value = ''

        // try to call component focus() first
        const comp = searchInputRef.value as any
        if (comp?.focus && typeof comp.focus === 'function') {
          comp.focus()
          return
        }

        // fallback: find the native input element and focus it
        const el = comp?.$el ?? comp
        const nativeInput = (el as HTMLElement | null)?.querySelector?.(
          'input'
        ) as HTMLInputElement | null
        nativeInput?.focus()
      })
    }
  }

  const resetAvailableTables = () => {
    availableTables.value = props.tableSchemas
    filteredTables.value = props.tableSchemas
    groupTablesByCategory()
  }

  // Watch for visibility changes to load tables if needed
  watch(
    () => props.visible,
    (newValue: boolean) => {
      handleVisibilityChange(newValue)
    }
  )

  watch(
    () => props.tableSchemas,
    (newItems) => {
      availableTables.value = [...newItems]
      filteredTables.value = [...newItems]
      groupTablesByCategory()
    }
  )

  defineExpose({
    availableTables
  })
</script>

<template>
  <Modal
    :open="visible"
    title="Add Table"
    width="1000px"
    :bodyStyle="{ minHeight: '60vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="table-selection-content">
      <!-- Search Bar -->
      <div class="mb-4 flex items-center pr-2">
        <Input
          ref="searchInputRef"
          v-model:value="searchText"
          placeholder="Search tables by name or description..."
          size="large"
          @input="onSearchTextChanged"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </Input>
        <Checkbox class="ml-2 flex-shrink-0 whitespace-nowrap" v-model:checked="showHiddenTables">
          Show All Tables
        </Checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="loadingTables" class="py-8 text-center">
        <Spin size="large" />
        <p class="mt-2">Loading available tables...</p>
      </div>

      <!-- Tables List -->
      <div v-else id="tablesListContainer" class="max-h-[60vh] overflow-y-auto">
        <div
          v-if="!hasErrorLoadingTables"
          v-for="(tables, category) in groupedTables"
          :key="category"
          class="mb-6"
        >
          <!-- Only show category if it has tables (after filtering) -->
          <template
            v-if="tables.filter((x) => (showHiddenTables ? x : x.is_visible === true)).length > 0"
          >
            <h3 class="mb-3 border-b pb-1 text-lg font-semibold">
              {{ category }}
              <span v-if="searchText.trim()" class="ml-2 text-sm font-normal">
                ({{ tables.length }} {{ tables.length === 1 ? 'table' : 'tables' }})
              </span>
            </h3>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Card
                v-for="table in tables.filter((x) =>
                  showHiddenTables ? x : x.is_visible === true
                )"
                :key="table.type"
                class="cursor-pointer transition-all duration-200 hover:border-blue-400 hover:shadow-md"
                size="small"
                @click="selectTable(table)"
              >
                <div class="p-2">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h4 class="mb-1 text-sm font-medium">{{ table.display_name }}</h4>
                      <p class="mb-2 text-xs dark:text-[var(--white-50)]">
                        {{ table.description }}
                      </p>
                      <div class="flex items-center text-xs">
                        <span
                          class="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {{ table.fields.length }} field{{ table.fields.length !== 1 ? 's' : '' }}
                        </span>
                      </div>
                    </div>
                    <PlusOutlined class="ml-2 text-blue-500" />
                  </div>
                </div>
              </Card>
            </div>
          </template>
        </div>
        <div
          v-else-if="hasErrorLoadingTables"
          class="mt-1 mt-[20vh] flex justify-center text-red-400"
        >
          <WarningFilled class="mr-2 text-2xl" />
          An error was encountered loading tables.
        </div>
        <!-- No Results -->
        <div v-if="!hasErrorLoadingTables && filteredTables.length === 0" class="py-8 text-center">
          <p>
            {{
              searchText.trim() ? 'No tables found matching your search.' : 'No tables available.'
            }}
          </p>
          <p v-if="searchText.trim()" class="mt-1 text-xs">
            Try adjusting your search terms or clear the search to browse all tables.
          </p>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-6 flex items-center justify-between border-t pt-4">
        <div class="text-sm">Click on any table above to add it to your tables pipeline</div>
        <div class="flex gap-2">
          <Button @click="handleCancel">Cancel</Button>
          <Button type="primary" @click="handleCancel">Close</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
  .table-selection-content {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  #tablesListContainer {
    flex: 1;
  }
  #tablesListContainer::-webkit-scrollbar {
    width: 6px;
  }
  #tablesListContainer::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  #tablesListContainer::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  #tablesListContainer::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
</style>
